import type { ReactNode } from 'react';
import { lazy, Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { UsePersistentReducerOptionsType } from '@/hooks/usePersistentReducer';
import usePersistentReducer from '@/hooks/usePersistentReducer';

import { env } from '@/config/env.config';
import { isDevtoolsEnvironment } from '@/utils/env.utils';

import type { DevtoolsContextType } from './DevtoolsStateType';
import type DevtoolsStateType from './DevtoolsStateType';
import devtoolsReducer from './Devtools.reducer';
import DevtoolsContext from './DevtoolsContext';

const ControllerDevtoolsComponent = lazy(() => import('./components'));

// The context remains available to consumers in every environment. Member-data
// controls and persistence are restricted to explicitly approved non-production builds.
const isDevtoolsEnabled = isDevtoolsEnvironment(env.VITE_ENVIRONMENT);

const initialState: DevtoolsStateType = {
  enabled: false,
  member: {},
};

function devtoolsInit(
  initData: DevtoolsStateType | undefined
): DevtoolsStateType {
  if (initData) {
    return {
      ...initialState,
      ...initData,
    };
  }

  return initialState;
}

function DevtoolsProvider({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  const useReducerOptions = useMemo<UsePersistentReducerOptionsType>(
    () => ({
      key: 'atlas_devtools',
      enabled: isDevtoolsEnabled,
    }),
    []
  );

  const [state, dispatch] = usePersistentReducer(
    devtoolsReducer,
    initialState,
    devtoolsInit,
    useReducerOptions
  );

  const value = useMemo((): DevtoolsContextType => {
    return {
      state,
      dispatch,
    };
  }, [dispatch, state]);

  return (
    <DevtoolsContext.Provider value={value}>
      {children}
      {isDevtoolsEnabled && (
        <ErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <ControllerDevtoolsComponent />
          </Suspense>
        </ErrorBoundary>
      )}
    </DevtoolsContext.Provider>
  );
}

export default DevtoolsProvider;
